import { RouteComponentProps } from "@reach/router"
import { toSortOrder } from "@shared/platform-api/requests"
import { DocumentTitleOverride, usePaginatedApi } from "@shared/primitive-components"
import {
  DateFilter,
  formatForApi,
  isDateFilter,
  parseDateRange,
  toDateRangeLabel,
} from "@shared/utils/formatters/dates"
import { updateQueryStringParams } from "@shared/utils/helpers/queryStringHelpers"
import { useQueryStringWithStateHook } from "@shared/utils/helpers/useQueryStringWithStateHooks"
import { memo, useState } from "react"
import {
  FindVehicleTripsInput,
  Trip,
  TripSortKeys,
  TripSortKeysFE,
  tripSortKeysMap,
  useFindVehicleTrips,
} from "../../../../api/insights"
import { ListWrapperWithContext } from "../../../../components/ListWrapperWithContext"
import { usePolicyAndFleet } from "../../../../contexts"
import {
  CompletedTripsTable,
  DateRange,
  TripCustomDate,
  tripCustomDateFilterCustomItem,
  tripCustomDateFilterItems,
  VehicleDetails,
} from "../../../components/SafetyInsightsComponents"

type PageProps = RouteComponentProps<{
  vrn: string
}>

const VehicleComponent = ({ vrn }: RouteComponentProps<PageProps>) => {
  const { policy } = usePolicyAndFleet()

  const [isOpen, setIsOpen] = useState(false)
  const defaultDateFilter = DateFilter.LAST_30_DAYS

  const [limit, setLimit] = useQueryStringWithStateHook(`limit`)
  const [sortOrder, setSortOrder] = useQueryStringWithStateHook(`sort_order`)
  const [sortKey, setSortKey] = useQueryStringWithStateHook(`sort_key`)
  const [fromDate, setFromDate] = useQueryStringWithStateHook(`from_date`, defaultDateFilter)
  const [toDate, setToDate] = useQueryStringWithStateHook(`to_date`)

  const queryStringAndStateSetters = {
    setLimit,
    setSortOrder,
    setSortKey,
    setFromDate,
    setToDate,
  }

  const { fromDate: parsedFromDate, toDate: parsedToDate } = parseDateRange(fromDate, toDate, defaultDateFilter)

  const input: FindVehicleTripsInput = {
    policyId: policy.id,
    limit: limit && /^[0-9]*$/.test(limit) ? parseInt(limit, 10) : 50,
    vrn: vrn ?? ``,
    sortKey: (sortKey || `finishedAt`) as TripSortKeys,
    sortOrder: toSortOrder(sortOrder),
    startedAtAfter: formatForApi(parsedFromDate),
    startedAtBefore: formatForApi(parsedToDate),
  }

  const [res, reload] = useFindVehicleTrips(input)
  const { page, dataCount, sort, setSort, pagination, mutateRequestInput } = usePaginatedApi<
    Trip,
    FindVehicleTripsInput,
    TripSortKeys,
    TripSortKeysFE
  >(input, reload, res.result, tripSortKeysMap, queryStringAndStateSetters)

  const Title = <VehicleDetails vrn={vrn || ``} />

  const setDateRange = (dateRange: DateRange) => {
    const { mutate, currentRequestInput } = mutateRequestInput
    const { fromDate: rawFromDate, toDate: rawToDate } = dateRange
    const { fromDate: newFromDate, toDate: newToDate } = parseDateRange(rawFromDate, rawToDate, defaultDateFilter)
    setFromDate(rawFromDate)
    mutate({
      ...currentRequestInput,
      startedAtAfter: formatForApi(newFromDate),
      startedAtBefore: formatForApi(newToDate),
    })
    updateQueryStringParams(
      {
        from_date: rawFromDate,
        to_date: rawToDate,
      },
      window.location
    )
  }

  return (
    <>
      <DocumentTitleOverride>Flock Motor | Safety Score - Vehicle</DocumentTitleOverride>
      <ListWrapperWithContext
        title={Title}
        subTitle="Completed Trips"
        subTitleTooltip="This table shows all the trip data that we have collected. The preset 30-day filter can be changed by using the filters."
        dataCount={dataCount}
        listApiStatus={{
          loading: res.loading || !res.result,
          failed: res.result?.tag === `err`,
        }}
        filter={{
          defaultDisplayValue: isDateFilter(fromDate) ? fromDate : toDateRangeLabel(parsedFromDate, parsedToDate),
          value: isDateFilter(fromDate) ? fromDate : undefined,
          action: (value: DateFilter) => {
            if (value !== DateFilter.CUSTOM) {
              setDateRange({ fromDate: value, toDate: null })
            } else {
              setIsOpen(true)
            }
          },
          items: tripCustomDateFilterItems,
          lastItem: tripCustomDateFilterCustomItem,
        }}
        totalLabels={{
          singular: `Trip`,
          plural: `Trips`,
        }}
      >
        <CompletedTripsTable
          page={page}
          pagination={pagination}
          sort={sort}
          sortData={setSort}
          dataCount={dataCount}
          isVehicle
        />
      </ListWrapperWithContext>
      <TripCustomDate
        dateRange={{ fromDate: fromDate as string, toDate }}
        setDateRange={setDateRange}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </>
  )
}

export const Vehicle = memo(VehicleComponent, (prevProps, nextProps) => prevProps.vrn !== nextProps.vrn)
